import {
  Box,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Applicant, QibDetailsType } from '../../redux-store/types/api-types';
import React, { useEffect } from 'react';
import { SubHeading } from '../investors/components';
import {
  QibQuestionType,
  QibValues,
  _Props,
  _initialQibValues,
  _singleQibQuestionType,
  shouldShowQuestion,
  shouldShowRemarks,
} from '../investors/qibChecklist';
import { YES_NO_MASTER } from '../../utils/constant';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { QibQue } from '../../redux-store/actions';

const QibForm: React.FC<{ values: QibValues }> = ({ values }) => {
  const showQ = (q: _singleQibQuestionType) => shouldShowQuestion(q, values);

  const showR = (q: _singleQibQuestionType) => shouldShowRemarks(q, values);

  return (
    <Box>
      {values.qibQue.map((section: QibQuestionType, i: number) => (
        <Box key={i} sx={{ mb: 4 }}>
          {/* Section Title */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            {section.section}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Response</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                    {section.remarkLabel ?? 'Remarks'}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {section.questions.map((q) => {
                  if (!showQ(q)) return null;

                  return (
                    <TableRow key={q.id}>
                      {/* Question Label */}
                      <TableCell sx={{ verticalAlign: 'top' }}>{q.label}</TableCell>

                      {/* Response */}
                      <TableCell>
                        {/* YES/NO */}
                        {q.type === 'yesno' && (YES_NO_MASTER[values.qib?.[q.id] ?? ''] || '--')}

                        {/* MULTI */}
                        {/* TEXT */}
                        {((q.type === 'multi' && q.options) || q.type === 'text') &&
                          (values.qib?.[q.id] ? values.qib?.[q.id] : '--')}
                      </TableCell>

                      {/* Remarks */}
                      <TableCell>
                        {showR(q)
                          ? values.qib?.[`${q.id}Remarks`]
                            ? values.qib?.[`${q.id}Remarks`]
                            : '--'
                          : '--'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default function QibCheckList({
  applicants,
}: {
  applicants: Partial<Applicant>[];
}): JSX.Element {
  const [initialQibValues, setInitialQibValues] = React.useState<QibValues>({
    qib: _initialQibValues,
    qibQue: [] as unknown as QibQuestionType[],
    saveType: 'save for later',
  });
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const [qibJson, setQibJson] = React.useState<QibQuestionType[]>([]);
  useEffect(() => {
    // Fetch existing data from API and set as initial values
    async function fetchQibData() {
      try {
        // Simulate API call
        const res = ((await dispatch(QibQue())) || []) as unknown as QibQuestionType[];
        setQibJson(res);
      } catch (error) {
        console.error('Error fetching QIB data:', error);
      }
    }

    fetchQibData();
  }, []);
  useEffect(() => {
    if (Object.keys(application?.qibDetail || {}).length > 0) {
      setInitialQibValues((prev) => ({
        ...prev,
        qib: {
          ...prev.qib,
          ...application?.qibDetail,
        } as QibDetailsType,
        qibQue: qibJson.filter(
          (section) =>
            section.applicationType === application?.applicationType || !section.applicationType
        ),
      }));
    }
  }, [application, qibJson]);
  return (
    <>
      <Box
        sx={{
          bgcolor: 'white',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: '10px',
        }}
        mt={5}>
        <Box sx={{ p: 2 }}>
          <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main' }}>
                Qib CheckList
              </Typography>
            </Grid>
            <QibForm values={initialQibValues} />
          </Grid>
        </Box>
      </Box>
    </>
  );
}
